// React
import React from 'react';
// redux hooks
import { useFieldArray, Controller } from 'react-hook-form';
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import FormHelperText from '@mui/material/FormHelperText';
import TextField from '@mui/material/TextField';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from '@mui/material/Typography';
// custom components
import NumberedHeading from '../../../../../../shared-components/forms/NumberedHeading';
import FPEQuestionPoolArray from './FPEQuestionPoolArray';
import WYSIWYGEditor from '../../../../../../shared-components/WYSIWYGEditor';
// data
import { ReviewData } from '../../../data/Defaults';
import { set } from 'lodash';

export default function FPETargetObjectArray({
  control,
  errors,
  inputDir,
  setValue,
  possiblePages
}) {
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: `content.core`
  });

  const objects = [
    { id: 'binder', label: 'Binder', device: 'paper' },
    { id: 'blue_book', label: 'Book, Blue', device: 'paper' },
    { id: 'purple_book', label: 'Book, Purple', device: 'paper' },
    { id: 'bookshelf', label: 'Bookshelf', device: 'paper' },
    { id: 'computer_monitor', label: 'Computer Monitor', device: 'browser' },
    { id: 'document_tray', label: 'Document Tray', device: 'paper' },
    { id: 'folders', label: 'Manilla Folders', device: 'paper' },
    { id: 'globe', label: 'Globe', device: 'paper' },
    { id: 'laptop', label: 'Laptop', device: 'browser' },
    { id: 'leather_notebook', label: 'Leather Notebook', device: 'paper' },
    {
      id: 'leather_notebook_black',
      label: 'Leather Notebook, Black',
      device: 'paper'
    },
    { id: 'world_map', label: 'Map, World', device: 'paper' },
    { id: 'phone', label: 'Phone', device: 'phone' },
    { id: 'mobile_phone', label: 'Phone, Mobile', device: 'phone' },
    { id: 'newspaper', label: 'Newspaper', device: 'paper' },
    { id: 'paper_stack', label: 'Stack of Papers', device: 'paper' },
    { id: 'tablet', label: 'Tablet', device: 'tablet' },
    { id: 'image_wall', label: 'Framed Image (wall)', device: 'default' },
    { id: 'projector', label: 'Projector', device: 'browser' },
    { id: 'door', label: 'Door', device: 'browser' },
    { id: 'picture_1', label: 'Picture 1', device: 'paper' },
    { id: 'picture_2', label: 'Picture 2', device: 'paper' },
    { id: 'picture_3', label: 'Picture 3', device: 'paper' }
  ];

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="pr-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="m-0 font-semibold">
                      Task Objective {index + 1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <NumberedHeading
                        number="1"
                        title="Object"
                        instructions="Select the interactive item."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.object.id`}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            className="mt-16"
                            error={!!errors.content?.core?.[index]?.object?.id}
                          >
                            <InputLabel id="select-object">
                              Interactable Object
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="select-object"
                              id="object"
                              label="Object"
                              classes={{
                                select: 'flex items-center space-x-12'
                              }}
                              defaultValue=""
                              onChange={(ev) => {
                                const object = objects.find(
                                  (obj) => obj.id === ev.target.value
                                );
                                setValue(
                                  `content.core.${index}.object.id`,
                                  object.id
                                );
                                setValue(
                                  `content.core.${index}.object.label`,
                                  object.label
                                );
                                setValue(
                                  `content.core.${index}.object.device`,
                                  object.device
                                );
                              }}
                            >
                              <MenuItem
                                value=""
                                disabled
                                className="space-x-12"
                              >
                                <span>Select an interactable object</span>
                              </MenuItem>
                              {objects.map((object, index) => (
                                <MenuItem
                                  key={index}
                                  value={object.id}
                                  className="space-x-12"
                                >
                                  {object.label}
                                </MenuItem>
                              ))}
                            </Select>
                            {!!errors.content?.core?.[index]?.object?.id ? (
                              <FormHelperText>
                                {
                                  errors.content?.core?.[index]?.object?.id
                                    ?.message
                                }
                              </FormHelperText>
                            ) : null}
                          </FormControl>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="2"
                        title="Task Title (TL)"
                        instructions="Enter the title of the task in the Target Language."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.task.title_tl`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Task Title (TL)"
                              placeholder="Task Title (TL)"
                              error={
                                !!errors.content?.core?.[index]?.task?.title_tl
                              }
                              helperText={
                                errors.content?.core?.[index]?.task?.title_tl
                                  ?.message
                              }
                              fullWidth
                              required
                              dir={inputDir}
                            />
                          </>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="3"
                        title="Task Title (EN)"
                        instructions="Enter the title of the task in English."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.task.title_en`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Task Title (EN)"
                              placeholder="Task Title (EN)"
                              error={
                                !!errors.content?.core?.[index]?.task?.title_en
                              }
                              helperText={
                                errors.content?.core?.[index]?.task?.title_en
                                  ?.message
                              }
                              fullWidth
                              required
                            />
                          </>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="4"
                        title="Task Description (TL)"
                        instructions="Fill in a description of the task in the target language."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.task.text_tl`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            error={errors.content?.core?.[index]?.task?.text_tl}
                          />
                        )}
                      />

                      <Divider className="my-16" />

                      <NumberedHeading
                        number="5"
                        title="Task Description (EN)"
                        instructions="Fill in a description of the task in english."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.task.text_en`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            error={errors.content?.core?.[index]?.task?.text_en}
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="6"
                        title="Task Type"
                        instructions="Select the type of task."
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.task.type`}
                        render={({ field }) => (
                          <FormControl
                            fullWidth
                            className="mt-16"
                            error={!!errors.content?.core?.[index]?.task?.type}
                          >
                            <InputLabel id="select-taskType">
                              Task Type
                            </InputLabel>
                            <Select
                              {...field}
                              labelId="select-taskType"
                              id="taskType"
                              label="Material Location"
                              classes={{
                                select: 'flex items-center space-x-12'
                              }}
                              defaultValue=""
                            >
                              <MenuItem
                                value=""
                                disabled
                                className="space-x-12"
                              >
                                <span>Select a task type</span>
                              </MenuItem>
                              <MenuItem value="main" className="space-x-12">
                                Main Task
                              </MenuItem>
                              <MenuItem
                                value="supplemental"
                                className="space-x-12"
                              >
                                Supplemental Task
                              </MenuItem>
                              <MenuItem
                                value="navigation"
                                className="space-x-12"
                              >
                                Navigation to Path
                              </MenuItem>
                            </Select>
                            {!!errors?.content?.core?.[index]?.task?.type ? (
                              <FormHelperText>
                                {
                                  errors?.content?.core?.[index]?.task?.type
                                    ?.message
                                }
                              </FormHelperText>
                            ) : null}
                          </FormControl>
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="7"
                        title="Task Content"
                        instructions="Using the controls below, Add the neccesary pages to this task"
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.content`}
                        render={({ field }) => (
                          <FPEQuestionPoolArray
                            nestIndex={index}
                            inputDir={inputDir}
                            {...{ control, errors }}
                            possiblePages={possiblePages}
                          />
                        )}
                      />
                    </Box>
                    <Box
                      className="flex items-center mt-32 p-0 border-t"
                      sx={{ backgroundColor: 'background.default' }}
                    >
                      {index !== 0 && (
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index - 1)}
                        >
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-up
                          </BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Up
                          </span>
                        </Button>
                      )}
                      {index !== fields.length - 1 && (
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index + 1)}
                        >
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-down
                          </BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Down
                          </span>
                        </Button>
                      )}
                      <Button
                        className="group flex items-center mt-2 py-2 px-4 rounded cursor-pointer ml-auto"
                        onClick={() => remove(index)}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:minus-circle
                        </BASESvgIcon>
                        <span className="ml-8 font-medium text-secondary group-hover:underline">
                          Remove Object
                        </span>
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-2 -ml-4 py-2 px-4 rounded cursor-pointer"
          onClick={() =>
            append({
              id: BASEUtils.generateGUID(),
              review: ReviewData,
              object: {
                id: '',
                label: '',
                device: ''
              },
              task: {
                title_tl: '',
                title_en: '',
                text_tl: '',
                text_en: '',
                type: ''
              },
              content: [
                {
                  id: '',
                  conditional: false,
                  conditional_id: '',
                  return: false,
                  next_id: ''
                }
              ]
            })
          }
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Object
          </span>
        </Button>
      </Box>
    </>
  );
}
